import PageBackTitle from "../../Component/PageBackTitle";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import API_URL from "../../../src/Config";
import axios from "axios";
import Select from "react-select";

function GasDetectorForm({ isSidebarExpanded }) {
  const [formData, setFormData] = useState({
    name: "",
    model_number: "",
    tag_no: "",
    frequency: "",
    location: "",
    last_calibration_date: null,
    description: "",
    service_code: "",
  });

  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [detectorTypes, setDetectorTypes] = useState([]);
  const [serviceCodes, setServiceCodes] = useState([]);
  const [loadingServiceCodes, setLoadingServiceCodes] = useState(false);
  
  const frequencyOptions = [
    { label: "1 Month", value: 30 },
    { label: "2 Months", value: 60 },
    { label: "3 Months", value: 90 },
    { label: "6 Months", value: 180 },
    { label: "12 Months", value: 365 },
  ];

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      type_id: selectedOption ? selectedOption.value : "",
    }));
    setErrors((prev) => ({ ...prev, type_id: null }));
  };

  const handleServiceCodeChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      service_code: selectedOption ? selectedOption.value : "",
    }));
    setErrors((prev) => ({ ...prev, service_code: null }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      installation_date: date,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type_id) newErrors.type_id = "Detector Type is required";
    if (!formData.frequency) newErrors.frequency = "Frequency is required";

    if (!formData.tag_no.trim()) newErrors.tag_no = "Tag No is required";

    if (!formData.last_calibration_date)
      newErrors.last_calibration_date = "Last Calibration Date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    const authKey = localStorage.getItem("authKey");

    // Fetch detector types
    axios
      .get(`${API_URL}/api/get_gas_detector_types`, {
        headers: {
          Authorization: authKey,
        },
      })
      .then((res) => {
        const options = res.data.gas_detector_types.map((item) => ({
          value: item.id,
          label: item.detected_gases,
        }));
        setDetectorTypes(options);
      })
      .catch((err) => console.error("Error fetching detector types:", err));

    // Fetch service codes
    setLoadingServiceCodes(true);
    axios
      .get(`${API_URL}/api/service_codes`, {
        headers: {
          Authorization: authKey,
        },
      })
      .then((res) => {
        const options = res.data.service_codes.map((item) => ({
          value: item.service_code,
          label: item.service_code,
        }));
        setServiceCodes(options);
      })
      .catch((err) => {
        console.error("Error fetching service codes:", err);
        setSeverity("error");
        setSnackbarMessage("Failed to load service codes");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setLoadingServiceCodes(false);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const authKey = localStorage.getItem("authKey");

    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      const payload = {
        type_id: formData.type_id,
        model_number: formData.model_number,
        tag_no: formData.tag_no,
        frequency: formData.frequency,
        location: formData.location,
        last_calibration_date: formatDate(formData.last_calibration_date),
        description: formData.description,
        service_code: formData.service_code,
      };

      const response = await axios.post(
        `${API_URL}/api/add_gas_detector`,
        payload,
        {
          headers: {
            Authorization: authKey,
          },
        }
      );

      if (response.status === 200) {
        setSeverity("success");
        setSnackbarMessage("Gas detector added successfully!");
        setSnackbarOpen(true);
        setTimeout(() => navigate("/gas-detectors"), 1500);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSeverity("error");
      setSnackbarMessage(
        error?.response?.data?.error || "Failed to add gas detector"
      );
      setSnackbarOpen(true);
    }
  };

  // Custom styles for react-select to show more information
  const customSelectStyles = {
    option: (provided, state) => ({
      ...provided,
      padding: '10px',
    }),
  };

  const formatOptionLabel = ({ label, project_name, qty, uom }) => (
    <div>
      <div style={{ fontWeight: '500' }}>{label}</div>
      {project_name && (
        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
          Project: {project_name} {qty && uom && `| Qty: ${qty} ${uom}`}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">Add Gas Detector</PageBackTitle>
              <div className="data-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            <form className="form-detail">
              <div className="new-data-form">
                <div className="form-group-select">
                  <label>
                    Detector Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={detectorTypes}
                    value={detectorTypes.find(
                      (option) => option.value === formData.type_id
                    )}
                    onChange={handleSelectChange}
                    placeholder="Select Detector Type"
                    isClearable
                    className={errors.type_id ? "error-select" : ""}
                  />
                  {errors.type_id && (
                    <div className="error-message">{errors.type_id}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Tag No. <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      name="tag_no"
                      value={formData.tag_no}
                      onChange={handleChange}
                      placeholder="Enter tag no."
                      className={errors.tag_no ? "error-input" : ""}
                    />
                  </div>
                  {errors.tag_no && (
                    <div className="error-message">{errors.tag_no}</div>
                  )}
                </div>

                <div className="form-group-select">
                  <label>Service Code</label>
                  <Select
                    options={serviceCodes}
                    value={serviceCodes.find(
                      (option) => option.value === formData.service_code
                    )}
                    onChange={handleServiceCodeChange}
                    placeholder={loadingServiceCodes ? "Loading service codes..." : "Select Service Code"}
                    isClearable
                    isSearchable
                    isLoading={loadingServiceCodes}
                    formatOptionLabel={formatOptionLabel}
                    styles={customSelectStyles}
                    className={errors.service_code ? "error-select" : ""}
                  />
                  {errors.service_code && (
                    <div className="error-message">{errors.service_code}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Model Number</label>
                  <div className="input-group">
                    <input
                      name="model_number"
                      value={formData.model_number}
                      onChange={handleChange}
                      placeholder="Enter model number"
                    />
                  </div>
                </div>

                <div className="form-group-select">
                  <label>
                    Calibration Frequency <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={frequencyOptions}
                    value={frequencyOptions.find(
                      (option) => option.value === formData.frequency
                    )}
                    placeholder="Select Calibration Frequency"
                    onChange={(selectedOption) =>
                      handleChange({
                        target: {
                          name: "frequency",
                          value: selectedOption.value,
                        },
                      })
                    }
                    className={errors.frequency ? "react-select-error" : ""}
                  />
                  {errors.frequency && (
                    <div className="error-message">{errors.frequency}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Installed Location</label>
                  <div className="input-group">
                    <input
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Enter detector installed location"
                      className={errors.location ? "error-input" : ""}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>
                  Last Calibration Date <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <DatePicker
                    selected={formData.last_calibration_date}
                    onChange={(date) => {
                      setFormData((prev) => ({
                        ...prev,
                        last_calibration_date: date,
                      }));
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        last_calibration_date: "",
                      }));
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select last calibration date"
                    className={`form-control ${
                      errors.last_calibration_date ? "error-input" : ""
                    }`}
                  />
                </div>
                  {errors.last_calibration_date && (
                    <div className="error-message">
                      {errors.last_calibration_date}
                    </div>
                  )}
              </div>
              <div className="form-group">
                <label>Description</label>
                <div className="input-group-text">
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter Description"
                    rows={5}
                  />
                </div>
              </div>
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="submit"
                onClick={handleSubmit}
              >
                Save
              </button>
              <button
                type="button"
                className="data-form-cancel-button"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default GasDetectorForm;